import { useTranslation } from "react-i18next";

import NotificationPreferenceRow from "../../../../shared/ui/notifications/NotificationPreferenceRow";
import { useReminderChannels, ReminderChannelsNotice } from "../../../notification";
import {
    REMINDER_HOUR_CHOICES,
    SchedulerSettings,
    SchedulerSettingsUpdate,
} from "../../domain/types";

/**
 * Every message the Scheduler sends, who it goes to, and how.
 *
 * Three kinds, each with the same questions: a new booking, a change to one
 * (a reschedule or a cancellation), and the reminder before it. Reminders
 * used to live beside the working hours; they sit here now so a therapist
 * looking for "what does Alchely send my patients" finds all of it at once.
 *
 * Each row shows the channel of the patient's copy — offered where this
 * deployment can send that kind on WhatsApp, stated as email where it cannot —
 * and a one-line summary of what is sent, when, to whom and how.
 *
 * Each control saves on change, like the rest of the Scheduler settings.
 */
export default function SchedulerNotificationSettings({
    settings,
    isSaving,
    saveFailed,
    onChange,
}: {
    settings: SchedulerSettings;
    isSaving: boolean;
    /** The last save was refused: a radio snapping back needs a reason. */
    saveFailed: boolean;
    onChange: (update: SchedulerSettingsUpdate) => void;
}) {
    const { t } = useTranslation();
    // One request for the whole section: the rows and the notice read the same answer.
    const channels = useReminderChannels();

    return (
        <section className="flex max-w-2xl flex-col gap-4">
            <div className="flex flex-col gap-1">
                <h2 className="text-base font-semibold">
                    {t("scheduler.settings.notifications.title")}
                </h2>
                <p className="text-sm text-content-tertiary">
                    {t("scheduler.settings.notifications.intro")}
                </p>
            </div>

            <NotificationPreferenceRow
                name="booking-notice"
                title={t("scheduler.settings.notifications.booking.title")}
                description={t("scheduler.settings.notifications.booking.hint")}
                enabled={settings.booking_notice_enabled}
                toPatient={settings.booking_notice_to_patient}
                toTherapist={settings.booking_notice_to_therapist}
                disabled={isSaving}
                onEnabledChange={(enabled) => onChange({ booking_notice_enabled: enabled })}
                onAudienceChange={({ toPatient, toTherapist }) =>
                    onChange({
                        booking_notice_to_patient: toPatient,
                        booking_notice_to_therapist: toTherapist,
                    })
                }
                channel={{
                    kind: "choice",
                    value: settings.booking_notice_channel,
                    whatsappAvailable: channels.whatsapp_notices.booking,
                    onChange: (booking_notice_channel) => onChange({ booking_notice_channel }),
                }}
                when={{ key: "scheduler.settings.notifications.booking.when" }}
            />

            <NotificationPreferenceRow
                name="change-notice"
                title={t("scheduler.settings.notifications.change.title")}
                description={t("scheduler.settings.notifications.change.hint")}
                enabled={settings.change_notice_enabled}
                toPatient={settings.change_notice_to_patient}
                toTherapist={settings.change_notice_to_therapist}
                disabled={isSaving}
                onEnabledChange={(enabled) => onChange({ change_notice_enabled: enabled })}
                onAudienceChange={({ toPatient, toTherapist }) =>
                    onChange({
                        change_notice_to_patient: toPatient,
                        change_notice_to_therapist: toTherapist,
                    })
                }
                channel={{
                    kind: "choice",
                    value: settings.change_notice_channel,
                    whatsappAvailable: channels.whatsapp_notices.change,
                    onChange: (change_notice_channel) => onChange({ change_notice_channel }),
                }}
                when={{ key: "scheduler.settings.notifications.change.when" }}
            />

            <NotificationPreferenceRow
                name="reminder"
                title={t("scheduler.settings.notifications.reminder.title")}
                description={t("scheduler.settings.remindersHint")}
                enabled={settings.reminder_enabled}
                toPatient={settings.reminder_to_patient}
                toTherapist={settings.reminder_to_therapist}
                disabled={isSaving}
                onEnabledChange={(enabled) => onChange({ reminder_enabled: enabled })}
                onAudienceChange={({ toPatient, toTherapist }) =>
                    onChange({
                        reminder_to_patient: toPatient,
                        reminder_to_therapist: toTherapist,
                    })
                }
                channel={{
                    kind: "choice",
                    value: settings.reminder_notice_channel,
                    whatsappAvailable: channels.whatsapp_notices.reminder,
                    onChange: (reminder_notice_channel) => onChange({ reminder_notice_channel }),
                }}
                when={{
                    key: "scheduler.settings.notifications.reminder.when",
                    values: { count: settings.reminder_hours_before },
                }}
            >
                <label className="flex max-w-xs flex-col gap-1 text-sm">
                    {t("scheduler.settings.reminderLeadTime")}
                    <select
                        value={settings.reminder_hours_before}
                        disabled={isSaving}
                        aria-label={t("scheduler.settings.reminderLeadTime")}
                        onChange={(event) =>
                            onChange({ reminder_hours_before: Number(event.target.value) })
                        }
                        className="rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-900"
                    >
                        {/* A lead time saved before these choices existed, or
                            set through the API, still has to appear — otherwise
                            the select would silently show the wrong value. */}
                        {leadTimeChoices(settings.reminder_hours_before).map((hours) => (
                            <option key={hours} value={hours}>
                                {t("scheduler.settings.reminderLeadTimeOption", { count: hours })}
                            </option>
                        ))}
                    </select>
                </label>
            </NotificationPreferenceRow>

            <p className="text-sm text-content-tertiary">
                {t("scheduler.settings.reminderContentNote")}
            </p>

            {/* Under every row rather than inside one: it answers "how does
                this reach them" for all three kinds at once. */}
            <ReminderChannelsNotice kind="session" channels={channels} />

            {settings.google_connected && (
                // Turning the patient's copy off here cannot stop Google: an
                // attendee on the calendar event gets Google's own invitation.
                <p className="text-sm text-content-tertiary">
                    {t("scheduler.settings.notifications.googleInviteNote")}
                </p>
            )}

            {saveFailed && (
                <p role="alert" className="text-sm text-status-critical">
                    {t("scheduler.settings.saveError")}
                </p>
            )}
        </section>
    );
}

/** The offered lead times, plus the saved one when it is not among them. */
function leadTimeChoices(current: number): number[] {
    const choices: number[] = [...REMINDER_HOUR_CHOICES];
    if (!choices.includes(current)) {
        choices.push(current);
    }
    return choices.sort((a, b) => a - b);
}
